import ApplicationController from './application_controller'

// Express Checkout Element controller for Apple Pay and Google Pay
// Uses Stripe's modern Express Checkout Element (replaces legacy Payment Request Button)
//
// This controller uses an existing PaymentIntent with capture_method: 'manual'
// The payment is NOT charged until the user confirms on the review page.
export default class extends ApplicationController {
  static targets = [
    "expressCheckout",
    "walletContainer",
    "divider",
    "message"
  ]

  static values = {
    publishableKey: String,
    clientSecret: String,  // The existing PaymentIntent's client secret
    returnUrl: String,
    totalAmount: Number,
    organizationName: String,
    paymentIntentId: String
  }

  connect() {
    console.log('PaymentWallet controller connected (Express Checkout Element)')

    if (!this.publishableKeyValue) {
      console.warn('PaymentWallet: No Stripe publishable key provided')
      return
    }

    if (!this.clientSecretValue) {
      console.warn('PaymentWallet: No client secret provided')
      return
    }

    if (typeof Stripe === 'undefined') {
      console.warn('PaymentWallet: Stripe.js not loaded')
      return
    }

    this.stripe = Stripe(this.publishableKeyValue)
    this.initExpressCheckout()
  }

  async initExpressCheckout() {
    try {
      // Create Elements instance using the EXISTING PaymentIntent's client secret
      // This ensures we use the PaymentIntent created server-side with capture_method: 'manual'
      this.elements = this.stripe.elements({
        clientSecret: this.clientSecretValue,
        appearance: {
          theme: 'stripe',
          variables: {
            borderRadius: '4px'
          }
        }
      })

      // Create Express Checkout Element
      const expressCheckoutElement = this.elements.create('expressCheckout', {
        buttonHeight: 48,
        buttonType: {
          applePay: 'plain',
          googlePay: 'plain'
        },
        buttonTheme: {
          applePay: 'black',
          googlePay: 'black'
        },
        layout: {
          maxColumns: 1,
          maxRows: 2
        },
        paymentMethods: {
          applePay: 'auto',
          googlePay: 'auto',
          link: 'never' // Disable Link for now
        }
      })

      // Check if any wallets are available before showing
      expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
        console.log('PaymentWallet: Available payment methods', availablePaymentMethods)

        if (availablePaymentMethods) {
          // Show the wallet container if any payment methods are available
          const hasWallets = availablePaymentMethods.applePay ||
                           availablePaymentMethods.googlePay

          if (hasWallets) {
            this.walletContainerTarget.classList.remove('d-none')
            this.dividerTarget.classList.remove('d-none')
          } else {
            this.hideWalletSection()
          }
        } else {
          this.hideWalletSection()
        }
      })

      // Handle the confirm event
      // Since we're using an existing PaymentIntent with capture_method: 'manual',
      // this will AUTHORIZE but NOT CAPTURE the payment
      expressCheckoutElement.on('confirm', async (event) => {
        await this.handleExpressCheckoutConfirm(event)
      })

      // Handle cancel
      expressCheckoutElement.on('cancel', () => {
        console.log('PaymentWallet: User cancelled')
      })

      // Handle errors
      expressCheckoutElement.on('loaderror', (event) => {
        console.error('PaymentWallet: Load error', event.error)
        this.hideWalletSection()
      })

      // Mount the element
      expressCheckoutElement.mount(this.expressCheckoutTarget)

    } catch (error) {
      console.warn('PaymentWallet: Error initializing Express Checkout', error)
      this.hideWalletSection()
    }
  }

  async handleExpressCheckoutConfirm(event) {
    try {
      this.showMessage('Processing...', 'info')

      // Confirm the payment using the existing PaymentIntent
      // Because the PaymentIntent has capture_method: 'manual', this will only AUTHORIZE
      // The actual capture happens on the review page
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrlValue
        },
        redirect: 'if_required'
      })

      if (error) {
        console.error('PaymentWallet: Error confirming payment', error)
        this.showMessage(error.message, 'danger')
        return
      }

      console.log('PaymentWallet: Payment confirmed', paymentIntent.status)

      // The payment should be in 'requires_capture' status now (authorized but not charged)
      if (paymentIntent.status === 'requires_capture' || paymentIntent.status === 'requires_confirmation') {
        // Store wallet payment info for the review page
        this.storeWalletInfo(paymentIntent)

        // Submit the form to proceed to review
        const form = this.element.closest('form')
        if (form) {
          // Add hidden field for payment intent
          this.addHiddenField(form, 'payment_intent_id', this.paymentIntentIdValue)

          // Add wallet type field
          const walletType = this.detectWalletType(paymentIntent)
          this.addHiddenField(form, 'wallet_type', walletType)

          // Add flag to indicate wallet payment
          this.addHiddenField(form, 'wallet_payment', 'true')

          // Mark this as a wallet payment to skip the payment controller's handleSubmit
          // The wallet has already confirmed the payment with Stripe
          form.dataset.walletPaymentConfirmed = 'true'

          // Remove the payment controller's submit handler to prevent interception
          // We need to submit directly to the server, bypassing the card payment JS
          form.removeAttribute('data-action')

          // Use native submit to bypass all Stimulus handlers
          // requestSubmit can still trigger event listeners, so use submit() directly
          form.submit()
        }
      } else if (paymentIntent.status === 'succeeded') {
        // This shouldn't happen with capture_method: 'manual', but handle it
        console.warn('PaymentWallet: Payment was captured immediately (unexpected)')
        this.showMessage('Payment processed. Redirecting...', 'success')

        const form = this.element.closest('form')
        if (form) {
          this.addHiddenField(form, 'payment_intent_id', this.paymentIntentIdValue)
          this.addHiddenField(form, 'wallet_payment', 'true')

          // Mark as wallet payment to skip payment controller
          form.dataset.walletPaymentConfirmed = 'true'

          // Remove the payment controller's submit handler to prevent interception
          form.removeAttribute('data-action')

          // Use native submit to bypass all Stimulus handlers
          form.submit()
        }
      } else {
        console.error('PaymentWallet: Unexpected payment status', paymentIntent.status)
        this.showMessage(`Payment status: ${paymentIntent.status}. Please try again.`, 'danger')
      }

    } catch (error) {
      console.error('PaymentWallet: Error in confirm handler', error)
      this.showMessage(error.message || 'Payment failed', 'danger')
    }
  }

  addHiddenField(form, name, value) {
    // Remove existing field if present
    const existing = form.querySelector(`input[name="${name}"]`)
    if (existing) existing.remove()

    const field = document.createElement('input')
    field.type = 'hidden'
    field.name = name
    field.value = value
    form.appendChild(field)
  }

  detectWalletType(paymentIntent) {
    // Try to detect the wallet type from the payment method
    if (paymentIntent.payment_method_types?.includes('card')) {
      // Check if it's a wallet payment by looking at the payment method details
      // This will be more reliable when we retrieve the payment method
      return 'wallet'
    }
    return 'card'
  }

  storeWalletInfo(paymentIntent) {
    // Store wallet info in sessionStorage for the review page
    const walletInfo = {
      paymentIntentId: paymentIntent.id,
      status: paymentIntent.status,
      timestamp: new Date().toISOString()
    }

    sessionStorage.setItem('walletPayment', JSON.stringify(walletInfo))
    console.log('PaymentWallet: Stored wallet info', walletInfo)
  }

  hideWalletSection() {
    if (this.hasWalletContainerTarget) {
      this.walletContainerTarget.classList.add('d-none')
    }
    if (this.hasDividerTarget) {
      this.dividerTarget.classList.add('d-none')
    }
  }

  showMessage(text, type = 'info') {
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = text
      this.messageTarget.className = `alert alert-${type} mt-3`
      this.messageTarget.classList.remove('d-none')

      if (type !== 'danger') {
        setTimeout(() => {
          this.messageTarget.classList.add('d-none')
        }, 3000)
      }
    }
  }
}
